import { useEffect, useState } from 'react'
import { Box, Button, Typography } from '@mui/material'
import { captureException } from '@sentry/browser'
import {
  getRequestError,
  isAppVersionInconsistentError,
} from '~/adapter/api/error'
import { routes } from '~/app/routes/paths'
import gotonSrc from '~/assets/goton/goton-aseri.svg'
import logoSrc from '~/assets/logo/logo.svg'
import { appMode } from '~/config'
import { useLogout } from '~/domain/logout/useLogout'
import {
  selectIsIpRestricted,
  useIpRestrictionStore,
} from '~/stores/ipRestriction/ipRestriction'
import {
  selectKioskAuthError,
  useKioskAuthErrorStore,
} from '~/stores/kioskAuthError/kioskAuthError'
import { IpRestrictionError } from '../IpRestrictionError/IpRestrictionError'
import { KioskAuthError } from '../KioskAuthError/KioskAuthError'
import { PageLayout } from '../PageLayout/PageLayout'
import { UpdateModal } from '../UpdateModal/UpdateModal'
import type { FallbackProps } from 'react-error-boundary'

export function GlobalError({ error }: FallbackProps) {
  const [isAppVersionConsistent, setIsAppVersionConsistent] = useState<
    boolean | undefined
  >(undefined)
  const { logout } = useLogout()
  // ErrorBoundary は GlobalLayout の外側にあるため、ここが描画されている間は GlobalLayout の
  // 全画面オーバーレイ（IP 制限 / kiosk 認証エラー）が出ない。kiosk では認証エラーのクエリも
  // throwOnError: true で ErrorBoundary に届くので、ここでも同じオーバーレイを被せる
  //（Dialog は portal で body 直下に出るため、配置場所は見た目に影響しない）。
  const isIpRestricted = useIpRestrictionStore(selectIsIpRestricted)
  const kioskAuthError = useKioskAuthErrorStore(selectKioskAuthError)
  useEffect(() => {
    if (error != null) {
      void getRequestError(error)
        .then(e => {
          if (isAppVersionInconsistentError(e)) {
            setIsAppVersionConsistent(false)
            // ここでエラーがキャッチされているということは、queryCache などの共通エラーハンドリングで、
            // Global な state 上の isAppVersionConsistent フラグは更新されているはず
            return
          }
          setIsAppVersionConsistent(true)
          // apiErrorはqueryClientでhandleするのでここではunexpectedErrorのみをキャプチャする
          if (e.type === 'unexpectedError') {
            captureException(error)
          }
        })
        .catch(() => {
          // getRequestError はエラーを吐かないように注意して実装しているはずだが、
          // 念の為 catch しておく
          setIsAppVersionConsistent(true)
        })
    } else {
      setIsAppVersionConsistent(true)
    }
  }, [error])

  if (isAppVersionConsistent === undefined) {
    return null
  }

  return (
    <PageLayout header={{ component: null }} mainLayoutType="center">
      <Box
        sx={{
          width: '100%',
          maxWidth: '400px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {isIpRestricted && <IpRestrictionError />}
        {!isIpRestricted && kioskAuthError !== null && <KioskAuthError />}
        {isAppVersionConsistent ? (
          <>
            <Box component="img" src={logoSrc} sx={{ width: '280px' }} />
            <Typography variant="h1" sx={{ mt: 6 }}>
              エラーが発生しました。
            </Typography>
            <Typography variant="body2" sx={{ mt: 2 }}>
              ホーム画面に戻り、もう一度操作してください。
              <br />
              問題が解決しない場合は、お手数ですが担当者にお問い合わせください。
            </Typography>
            <Box component="img" src={gotonSrc} sx={{ width: '50%', mt: 3 }} />
            <Button
              onClick={() => {
                location.replace(routes.home.path)
              }}
              variant="contained"
              sx={{ width: '100%', mt: 2 }}
              size="large"
            >
              ホームに戻る
            </Button>
            {/* キオスク端末（kiosk）にはログアウトの概念が無いため出さない。「ホームに戻る」で復帰させる */}
            {appMode.isApp && (
              <Button
                onClick={async () => {
                  await logout({ userInitiated: true })
                }}
                variant="contained"
                color="secondary"
                sx={{ width: '100%', mt: 2 }}
                size="large"
              >
                ログアウト
              </Button>
            )}
          </>
        ) : (
          <UpdateModal />
        )}
      </Box>
    </PageLayout>
  )
}
